/**
 * Higher-level offline cache API used by Modules.jsx, ModuleScreen.jsx,
 * ReadingMode.jsx, RecordingMode.jsx.
 *
 * The contract:
 *   - downloadModule(module, onProgress)  → fetches every audio URL + the
 *     module image, then persists the module JSON. Calls onProgress({ done, total }).
 *   - resolveAudioSrc(url)                → returns an object-URL if the asset
 *     is cached, otherwise the original URL (network).
 *   - isOffline()                         → uses navigator.onLine.
 *   - removeModule(code)                  → deletes the module + every blob it
 *     uniquely owned. Blobs shared by other downloaded modules survive.
 */
import { ttsAudioUrl, getModule as fetchModuleApi, API } from "./apiClient";
import {
  putModule, getModule, deleteModule, listModules,
  putBlob, getBlob, deleteBlob, listAllBlobUrls,
} from "./offlineDb";

// Use the same default voice as the player so URLs match exactly.
const DEFAULT_VOICE = "onyx";

/** Collect every cacheable URL for a module (TTS, narrated audio, image). */
export function collectAudioUrls(module) {
  const urls = new Set();

  // Reading-mode (1-7) + MCQ (8): items with `ar` → TTS, items with narrated_audio_url
  for (const it of module.items || []) {
    if (it.ar) urls.add(ttsAudioUrl(it.ar, DEFAULT_VOICE));
    if (it.narrated_audio_url) urls.add(`${process.env.REACT_APP_BACKEND_URL}${it.narrated_audio_url}`);
    if (it.picture_url) urls.add(`${process.env.REACT_APP_BACKEND_URL}${it.picture_url}`);
    // MCQ external audio
    if (it.audio_url) urls.add(it.audio_url);
  }

  // Reading-mode (1-7): Learn-by-Group narrated audio
  for (const g of module.groups || []) {
    if (g.narrated_audio_url) urls.add(`${process.env.REACT_APP_BACKEND_URL}${g.narrated_audio_url}`);
  }

  // Recording mode (9): passages
  for (const p of module.passages || []) {
    if (p.ar) urls.add(ttsAudioUrl(p.ar, DEFAULT_VOICE));
    if (p.narrated_audio_url) urls.add(`${process.env.REACT_APP_BACKEND_URL}${p.narrated_audio_url}`);
    if (p.audio_url) urls.add(p.audio_url);
  }

  // Hero image
  if (module.hero_image_id) urls.add(`${API}/modules/${module.code}/image`);

  return Array.from(urls);
}

async function fetchBlob(url) {
  // Cross-origin GET — backend allows CORS so this works.
  const r = await fetch(url, { credentials: "include" });
  if (!r.ok) throw new Error(`Fetch failed (${r.status}) for ${url}`);
  const blob = await r.blob();
  return { blob, mime: blob.type || "audio/mpeg" };
}

/**
 * Estimate the total size of a module *without* downloading anything yet.
 * We can't know audio size up front (server is dynamic) without HEAD; so we
 * provide a rough per-URL estimate (avg 25 KB per TTS clip, 200 KB per
 * narrated audio, 80 KB per image, 150 KB per MCQ audio).
 */
export function estimateModuleBytes(module) {
  const urls = collectAudioUrls(module);
  let total = 0;
  for (const url of urls) {
    if (url.includes("/api/tts/audio")) total += 25 * 1024;
    else if (url.includes("/items/") || url.includes("/passages/")) total += 200 * 1024;
    else if (url.includes("/image")) total += 80 * 1024;
    else total += 150 * 1024;
  }
  // Plus the module JSON itself (~5 KB)
  return total + 5 * 1024;
}

/**
 * Download a module + every audio asset + hero image into IndexedDB.
 * Failures on individual blobs are tolerated — the rest still cache.
 */
export async function downloadModule(module, onProgress) {
  const urls = collectAudioUrls(module);
  let bytes = 0;
  let done = 0;
  const total = urls.length + 1; // +1 for the module record itself
  onProgress?.({ done, total, bytes });

  for (const url of urls) {
    try {
      const existing = await getBlob(url);
      if (existing) { bytes += existing.size; done += 1; onProgress?.({ done, total, bytes }); continue; }
      const { blob, mime } = await fetchBlob(url);
      await putBlob({ url, blob, size: blob.size, mime });
      bytes += blob.size;
    } catch (e) {
      // Non-fatal — log and keep going
      // eslint-disable-next-line no-console
      console.warn("offline cache: skipping", url, e?.message);
    }
    done += 1;
    onProgress?.({ done, total, bytes });
  }

  // Persist the module JSON last so a partial failure leaves a clean state.
  const record = {
    code: module.code,
    n: module.n,
    name: module.name,
    name_ar: module.name_ar,
    data: module, // full module payload
    audio_urls: urls,
    size_bytes: bytes,
    downloaded_at: new Date().toISOString(),
  };
  await putModule(record);
  done += 1;
  onProgress?.({ done, total, bytes, complete: true });
  return record;
}

/**
 * Delete a downloaded module. Blobs that no other downloaded module uses are
 * also evicted to reclaim space.
 */
export async function removeModule(code) {
  const rec = await getModule(code);
  if (!rec) return false;

  // Build a set of URLs still in use by OTHER downloaded modules.
  const others = (await listModules()).filter((m) => m.code !== code);
  const inUse = new Set();
  for (const o of others) {
    for (const u of o.audio_urls || []) inUse.add(u);
  }

  // Evict orphans owned only by this module.
  for (const u of rec.audio_urls || []) {
    if (!inUse.has(u)) await deleteBlob(u);
  }
  await deleteModule(code);
  return true;
}

/**
 * Return an object-URL for a cached asset (creates a fresh one on each call —
 * caller is responsible for revoking, OR can let the page-unload handle it).
 * Returns the ORIGINAL url if no cached blob exists (online path).
 */
export async function resolveAudioSrc(url) {
  try {
    const rec = await getBlob(url);
    if (rec?.blob) return URL.createObjectURL(rec.blob);
  } catch (_e) { /* fall through to online */ }
  return url;
}

export async function isModuleDownloaded(code) {
  const rec = await getModule(code);
  return Boolean(rec);
}

export async function getDownloadedModule(code) {
  const rec = await getModule(code);
  return rec?.data || null;
}

export async function listDownloads() {
  const rows = await listModules();
  return rows.sort((a, b) => (a.n || 0) - (b.n || 0));
}

/** Sum bytes across all cached blobs (uses .size on each record). */
export async function totalCachedBytes() {
  const rows = await listModules();
  return rows.reduce((s, r) => s + (r.size_bytes || 0), 0);
}

/** Free disk estimate via Storage API (may be undefined on older browsers). */
export async function storageEstimate() {
  if (typeof navigator !== "undefined" && navigator.storage?.estimate) {
    try { return await navigator.storage.estimate(); } catch (_e) { /* unsupported */ }
  }
  return { usage: undefined, quota: undefined };
}

export function formatBytes(n) {
  if (!n || n < 1024) return `${n || 0} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** Total blobs currently in the DB — handy for the Dashboard storage chip. */
export async function _debugBlobCount() {
  return (await listAllBlobUrls()).length;
}
